import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataService, ServicecallService } from 'src/app/services';

@Component({
  selector: 'app-common-line',
  templateUrl: './common-line.component.html',
  styleUrl: './common-line.component.css'
})
export class CommonLineComponent implements OnChanges, OnDestroy {
  @Input() entityDetails: any;
  @Input() widgetData: any;
  @Input() entityId: string;
  @Input() widgetType: string;
  @Input() fromDate: string;
  @Input() toDate : string;

  lineChartData: any = {};
  attributeData: any;
  isloader: boolean = true;
  isError: boolean = false;

  private _getEntityAttributeSubscription: Subscription;
  private _subscription: Subscription;
  private _intervalTimer: any;
  private _chartThreshold: any;


  constructor(private _service: ServicecallService, private _datePipe: DatePipe, private _dataSer: DataService) {
    this._subscription = this._dataSer?.events?.subscribe((res: any) => {
      if (res.name === 'apiRefresh') {
        this._getAttributeDetails();
      }
    });
  }

  ngOnDestroy(): void {
    this._getEntityAttributeSubscription?.unsubscribe();
    this._subscription?.unsubscribe();
    this._clearTimer();
  }

  ngOnChanges(): void {
    this._chartThreshold = this._service?.getConfigurationBasedOnCategory(this.entityDetails) ? this._service?.getConfigurationBasedOnCategory(this.entityDetails)?.chartThreshold : null;
    this._getAttributeDetails();
  }

  /**
   * Gets attribute details
   */
  private _getAttributeDetails() {
    const reqData = {
      entityId: this.entityId,
      from: this._datePipe.transform(this.fromDate ?? (new Date(new Date().setMinutes(new Date().getMinutes() - 60))), 'yyyy-MM-ddTHH:mm:ssZZ'),
      to: this._datePipe.transform(this.toDate ?? (Date.now()), 'yyyy-MM-ddTHH:mm:ssZZ'),
      siteId: this._service.getSiteId(),
      attributes: this.widgetData?.widgetDataAttributes?.map(attribute => attribute?.attributeName),
      includeHistory: false
    }
    this._getEntityAttributeSubscription = this._service?.getEntityAttribute(reqData)?.subscribe({
      next: (resp: any) => {
        if (resp?.status) {
          if (resp?.response?.data) {
            this.attributeData = resp?.response?.data?.attributes;
            if (Object.keys(this.attributeData)?.length) {
              // const chartDataSeries = this.attributeData[this.widgetData?.widgetDataAttributes?.[0]?.attributeName]?.map(attribute => attribute?.value);
              let chartDataSeries = this.attributeData[this.widgetData?.widgetDataAttributes?.[0]?.attributeName]?.map(attribute => [new Date(attribute?.timestamp), attribute?.value] );
              chartDataSeries = chartDataSeries.sort((a, b) => a?.[0] - b?.[0]);
              this._setChartContent(chartDataSeries, true);
            } else {
              this.attributeData = {};
              const today = new Date();
              this._setChartContent([[new Date(this.toDate) ?? today,0]], false);
            }
          } else {
            const today = new Date();
            this._setChartContent([[new Date(this.toDate) ?? today,0]], false);
            this.attributeData = {};
          }
        }
        this.isloader = false;
      },
      error: () => {
        this.isloader = false;
        this.isError = true;
        this.attributeData = {};
        this._refreshApi();
      }
    })
  }

  /**
   * Sets chart content
   * @param series 
   * @param xAxis 
   */
  private _setChartContent(series: any[], isDataAvailable: boolean) {
    this.lineChartData = {
      xAxis: {
        type: 'time',
        axisTick: {
          show: false
        },
        axisLine: {
          show: true
        },
        splitNumber: 2,
        hideOverlap: true,
        axisLabel: {
          show: true,
          formatter: (_value: number, index: number) => {
            return (isDataAvailable 
              ? (index === 1 || index === 2) 
              : index === 1
            )
              ? new Date(_value).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })
              : '';
          },
          textStyle: {
            fontSize: 12,
            fontFamily: 'Arial'
          }
        }
      },
      yAxis: {
        type: 'value',
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          formatter: (value: number) => Math.floor(value)?.toString(),
          textStyle: {
            fontSize: 12,
            fontFamily: 'Arial'
          }
        }
      },
      series: [
        {
          data: series,
          showSymbol: true,
          tooltip: {
            show: true,
            formatter: (params: any) => {
              const date = new Date(params.data[0]);
              const formattedDate = date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
              return `${formattedDate}, ${params.data[1]}`;
            },
            textStyle: {
              fontSize: 10,
              color: '#555'
            }
          },
          type: 'line',
          lineStyle: {
            color: '#32A7D0'
          },
          itemStyle: {
            color: '#32A7D0'
          },
          markLine: {
            data: [
              ...(this._chartThreshold?.[this.widgetData?.name]?.thresholdValues?.upperThreshold !== undefined
                ? [{ yAxis: this._chartThreshold[this.widgetData?.name].thresholdValues.upperThreshold, name: 'Upper Threshold' }]
                : []),
              ...(this._chartThreshold?.[this.widgetData?.name]?.thresholdValues?.lowerThreshold !== undefined
                ? [{ yAxis: this._chartThreshold[this.widgetData?.name].thresholdValues.lowerThreshold,  name: 'Lower Threshold' }]
                : [])
            ],
            lineStyle: {
              type: 'solid',
              color: 'rgba(250, 174, 165, 1)',
              width: 1
            },
            symbol: 'none',
            label: {
              show: false
            },
            tooltip: {
              show: true,
              formatter: function (params) {
                return `${params.name}: ${params.value}`;
              }
            }
          }
        }
      ],
      grid: {
        top: '10%',
        left: '12%',
        right: '5%',
        bottom: '8%'
      }
    }
    this._refreshApi();
  }

  /**
 * Refreshs api
 */
  private _refreshApi() {
    if (+this.widgetData?.refreshRate) {
      this._clearTimer();
      this._intervalTimer = setInterval(() => {
        this._getAttributeDetails();
      }, +this.widgetData?.refreshRate);
    }
  }

  /**
   * Clears timer
   */
  private _clearTimer() {
    if (this._intervalTimer) {
      clearInterval(this._intervalTimer);
      this._intervalTimer = null;
    }
  }

}
